import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import addHours from 'date-fns/addHours';
import es from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useState, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTaskContext } from '../../shared/context/taskContext';
import { useNavigate } from 'react-router';
import { Box, Typography } from '@mui/material';

const locales = {
  'es': es,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const messages = {
  allDay: 'Todo el día',
  previous: 'Anterior',
  next: 'Siguiente',
  today: 'Hoy',
  month: 'Mes',
  week: 'Semana',
  day: 'Día',
  agenda: 'Agenda',
  date: 'Fecha',
  time: 'Hora',
  event: 'Evento',
  noEventsInRange: 'No hay eventos en este rango',
  showMore: total => `+ Ver más (${total})`
};



const colorStatus = {
  pending: '#5dade2',
  in_progress: '#4218e0',
  review: '#ed6c02',
  completed: '#148f77',
}

export default function Calendar() {
  const theme = useTheme();
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('month');

  const navigate = useNavigate()

  const { tasksCalendar, loadingTasksCalendar, getTasksCalendar } = useTaskContext()

  useEffect(() => {
    getTasksCalendar()
  }, [])

  // Convertir los timestamps a objetos Date
  const formattedEvents = useMemo(() => {
    if (!tasksCalendar) return [];
    
    return tasksCalendar.map(event => ({
      ...event,
      start: new Date(event.start),
      end: new Date(event.end)
    }));
  }, [tasksCalendar]);

  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: colorStatus[event.status] || theme.palette.primary.main,
        borderColor: colorStatus[event.status] || theme.palette.primary.main
      }
    };
  };

  const handleNavigate = (newDate, view, action) => {
    console.log('Navegación:', { newDate, view, action });
    setDate(newDate);
    
    // Actualizar la lista de tareas cuando cambie el mes
    getTasksCalendar({
      newDate: newDate,
      view: view,
      action: action
    });
  };

  const handleViewChange = (newView) => {
    console.log('Cambio de vista:', newView);
    setView(newView);
    
    // Actualizar la lista de tareas cuando cambie la vista
    getTasksCalendar();
  };

  const handleSelectEvent = (event) => {
    console.log('Evento seleccionado:', event);
    // Aquí puedes agregar la lógica que necesites cuando se hace clic en un evento
    // Por ejemplo, abrir un modal, navegar a otra página, etc.

    if (event.type === 'task') {
      //navigate(`/task/wo/create/${event.hash_code}`)
    } else if (event.type === 'ot') {
      navigate(`/task/wo/detail-ot/${event.hash_code}`)
    }
  };


  // Estilos modernos
  const themeStyles = `
    .rbc-calendar {
      background-color: ${theme.palette.background.paper};
      color: ${theme.palette.text.primary};
      border-radius: 12px;
      overflow: hidden;
      box-shadow: ${theme.palette.shadow.default};
    }
    
    .rbc-toolbar {
      background-color: ${theme.palette.background.paper2};
      padding: 16px;
      border-radius: 12px 12px 0 0;
      border: none;
      margin: 0;
    }
    
    .rbc-toolbar button {
      color: ${theme.palette.text.primary};
      background-color: transparent;
      border: 1px solid ${theme.palette.primary.main};
      padding: 8px 16px;
      margin: 0 4px;
      border-radius: 8px;
      font-weight: 500;
      transition: all 0.2s ease;
    }
    
    .rbc-toolbar button:hover {
      background-color: ${theme.palette.primary.main};
      color: white;
      border-color: ${theme.palette.primary.main};
    }
    
    .rbc-toolbar button:active,
    .rbc-toolbar button.rbc-active {
      background-color: ${theme.palette.primary.main} !important;
      color: white !important;
      border-color: ${theme.palette.primary.main} !important;
    }
    
    .rbc-header {
      padding: 12px 8px;
      font-weight: 600;
      background-color: ${theme.palette.background.paper2};
      color: ${theme.palette.text.primary};
      border-bottom: 1px solid ${theme.palette.divider};
    }
    
    .rbc-today {
      background-color: ${theme.palette.background.medium} !important;
    }
    
    .rbc-event {
      background-color: ${theme.palette.primary.main};
      border: none !important;
      border-radius: 6px;
      padding: 2px 6px !important;
      margin: 1px 0 !important;
      font-size: 0.85rem !important;
      min-height: 22px !important;
      max-height: 22px !important;
      line-height: 18px !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      font-weight: 500;
      box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    }
    
    .rbc-event:hover {
      filter: brightness(0.9);
      transform: translateY(-1px);
      transition: all 0.2s ease;
      z-index: 100;
    }

    .rbc-event-content {
      font-size: 0.85rem !important;
      height: 18px !important;
      line-height: 18px !important;
    }
    
    .rbc-off-range-bg {
      background-color: ${theme.palette.background.default};
    }
    
    .rbc-month-view,
    .rbc-time-view {
      border: 1px solid ${theme.palette.divider};
      border-radius: 0 0 12px 12px;
    }
    
    .rbc-month-row {
      min-height: 150px !important;
    }

    .rbc-row-content {
      margin: 0 !important;
    }

    .rbc-row-segment {
      padding: 1px 2px !important;
    }
    
    .rbc-day-bg + .rbc-day-bg {
      border-left: 1px solid ${theme.palette.divider};
    }
    
    .rbc-month-row + .rbc-month-row {
      border-top: 1px solid ${theme.palette.divider};
    }
    
    .rbc-date-cell {
      padding: 4px;
      font-weight: 500;
    }
    
    .rbc-show-more {
      background-color: transparent !important;
      color: ${theme.palette.primary.main} !important;
      font-weight: 500;
      padding: 2px 6px !important;
      font-size: 0.85rem !important;
      margin: 2px 4px !important;
      border-radius: 4px;
      transition: all 0.2s ease;
      height: 20px !important;
      line-height: 16px !important;
    }

    .rbc-show-more:hover {
      background-color: ${theme.palette.primary.main} !important;
      color: white !important;
    }

    .rbc-overlay {
      background-color: ${theme.palette.background.paper} !important;
      border-radius: 8px !important;
      border: 1px solid ${theme.palette.divider} !important;
      box-shadow: ${theme.palette.shadow.default} !important;
      padding: 8px !important;
      z-index: 1000 !important;
    }

    .rbc-overlay-header {
      padding: 8px !important;
      color: ${theme.palette.text.primary} !important;
      border-bottom: 1px solid ${theme.palette.divider} !important;
      font-weight: bold !important;
    }

    .rbc-event.rbc-overlay {
      padding: 4px 8px !important;
      margin: 2px 8px !important;
      min-height: 24px !important;
      max-height: none !important;
    }

    /* Ajuste de opacidad para líneas en modo oscuro */
    .rbc-time-view * {
      border-color: ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider} !important;
    }

    /* Estilos para la vista de tiempo */
    .rbc-time-content {
      border-top: 1px solid ${theme.palette.divider};
    }

    .rbc-time-header-content {
      border-left: 1px solid ${theme.palette.divider};
    }

    .rbc-time-header-gutter {
      background-color: ${theme.palette.background.paper2};
    }

    .rbc-timeslot-group {
      border-bottom: 1px solid ${theme.palette.divider};
    }

    .rbc-time-slot {
      border-top: 1px solid ${theme.palette.divider};
    }

    .rbc-current-time-indicator {
      background-color: ${theme.palette.primary.main};
    }
  `;

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      height: '100%',
      minHeight: 0,
      overflow: 'hidden'
    }}>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          p: 1,
          backgroundColor: theme.palette.background.paper,
          borderRadius: '12px',
          boxShadow: theme.palette.shadow.default,
          flexShrink: 0
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', alignSelf: 'center' }}>
          Estados:
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: colorStatus.pending,
                borderRadius: '4px'
              }}
            />
            <Typography variant="body2">Pendiente</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: colorStatus.in_progress,
                borderRadius: '4px'
              }}
            />
            <Typography variant="body2">En Progreso</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: colorStatus.review,
                borderRadius: '4px'
              }}
            />
            <Typography variant="body2">En Revisión</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: colorStatus.completed,
                borderRadius: '4px'
              }}
            />
            <Typography variant="body2">Completado</Typography>
          </Box>
        </Box>
      </Box>
      <style>{themeStyles}</style>
      <Box sx={{ 
        flex: 1,
        minHeight: 0,
        '.rbc-calendar': {
          height: '100% !important'
        }
      }}>
        <BigCalendar
          events={formattedEvents}
          localizer={localizer}
          defaultDate={new Date()}
          date={date}
          onNavigate={handleNavigate}
          view={view}
          onView={handleViewChange}
          startAccessor="start"
          endAccessor="end"
          messages={messages}
          culture="es"
          style={{
            height: '100%'
          }}
          eventPropGetter={eventStyleGetter}
          views={['month']}
          popup={true}
          selectable
          onSelectEvent={handleSelectEvent}
          components={{
            month: {
              dateHeader: ({ date, label }) => (
                <span style={{ fontWeight: 'bold' }}>{label}</span>
              )
            }
          }}
          doShowMoreDrillDown={true}
          length={4}
          showMultiDayTimes
          max={6}
          step={60}
          timeslots={1}
        />
      </Box>
    </Box>
  );
}